import { useStore } from 'zustand';
import type { Game } from '@/game/Game';
import { useTaskCards } from '@/ui/hooks/useTaskCards';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { catalogNewIds } from '@/core/domain/economy';
import { useSave, useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import { Icon, type IconName } from './Icon';
import styles from './NavButtons.module.css';
import { currentRoom } from '@/ui/util/room';

export type Panel =
  | 'none'
  | 'catalog'
  | 'tasks'
  | 'orders'
  | 'stock'
  | 'menu'
  | 'achievements'
  | 'settings'
  | 'credits'
  | 'mail'
  | 'album'
  | 'photos'
  | 'photo'
  | 'profile'
  | 'shortcuts'
  | 'town'
  | 'unlocks'
  | 'studio';

const ICONS = {
  home: 'home',
  store: 'shop_room',
  town: 'town',
  ideas: 'ideas',
  shop: 'shopping',
} as const satisfies Record<string, IconName>;

function TabIcon({ name }: { name: IconName }) {
  return <Icon name={name} size={30} className={styles.icon} />;
}

/**
 * The main navigation (ux-screens §2.2, §6.1): a tab bar at the bottom on portrait phones, a rail on the left
 * on tablets and landscape phones, tabs in the top bar from 1200 px. Nhà / Tiệm / Thị trấn change place and
 * close any sheet; Gợi ý and Mua sắm open their sheet, and tapping the open one again closes it.
 */
export function NavBar({
  game,
  panel,
  onOpen,
  onClose,
}: {
  game: Game;
  panel: Panel;
  onOpen: (panel: Panel) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { content } = useServices();
  // A quiet dot on Mua sắm when something newly unlocked has not been looked at (ux-screens §6.1).
  const shopNew = useSaveDerived((s) => catalogNewIds(content, s).length > 0);
  const busy = useStore(game.miniGameUi, (s) => s.run !== null);
  const room = useSave(currentRoom);
  const hasShop = useSave((s) => s.scenes.includes('minimart'));
  const hasTown = useSave((s) => s.scenes.includes('townmap'));
  // Studio NANO is reached through Thị trấn: a quiet dot there while a booking waits (studio-nano.md §2).
  const studioWaiting = useSave(
    (s) => s.lastSession.sceneId !== 'studio' && (s.studio.requests.length > 0 || s.studio.session !== null),
  );
  const { cards } = useTaskCards();
  // A dot only when something can be claimed — never for new cards (no pressure, ux-screens §3.8).
  const claimable = cards.some((c) => c.kind !== 'tutorial' && c.status === 'completed');
  if (busy || panel === 'photo') return null;

  const sheetTab = (target: Panel) => () => {
    if (panel === target) onClose();
    else onOpen(target);
  };
  const roomTab = (target: 'home' | 'minimart') => () => {
    onClose();
    if (room !== target) void game.goToRoom(target);
  };
  const current = (on: boolean) => (on ? ('page' as const) : undefined);
  const placeOpen = panel === 'none';
  return (
    <nav className={styles.bar} aria-label={t('ui.nav.label')} data-testid="main-nav">
      <button
        type="button"
        className={styles.tab}
        data-testid="nav-room-home"
        aria-current={current(room === 'home' && placeOpen)}
        onClick={roomTab('home')}
      >
        <TabIcon name={ICONS.home} />
        <span>{t('ui.nav.home')}</span>
      </button>
      {hasShop && (
        <button
          type="button"
          className={styles.tab}
          data-testid="nav-room-minimart"
          aria-current={current(room === 'minimart' && placeOpen)}
          onClick={roomTab('minimart')}
        >
          <TabIcon name={ICONS.store} />
          <span>{t('ui.nav.shop_room')}</span>
        </button>
      )}
      {hasTown && (
        <button
          type="button"
          className={styles.tab}
          data-testid="nav-town"
          aria-current={current(panel === 'town')}
          onClick={sheetTab('town')}
        >
          <TabIcon name={ICONS.town} />
          <span>{t('ui.town.title')}</span>
          {studioWaiting && <span className={styles.dot} aria-label={t('ui.studio.waiting')} />}
        </button>
      )}
      <button
        type="button"
        className={styles.tab}
        data-testid="nav-tasks"
        aria-current={current(panel === 'tasks')}
        onClick={sheetTab('tasks')}
      >
        <TabIcon name={ICONS.ideas} />
        <span>{t('ui.nav.tasks')}</span>
        {claimable && <span className={styles.dot} aria-label={t('ui.tasks.claimable')} />}
      </button>
      <button
        type="button"
        className={styles.tab}
        data-coach-id="nav-shop"
        data-testid="nav-shop"
        aria-current={current(panel === 'catalog' || panel === 'stock')}
        onClick={sheetTab('catalog')}
      >
        <TabIcon name={ICONS.shop} />
        <span>{t('ui.nav.shop')}</span>
        {shopNew && <span className={styles.dot} aria-label={t('ui.catalog.has_new')} />}
      </button>
    </nav>
  );
}

/** What only makes sense in the Tiệm: open/close the door and the orders board, floating above the room. */
export function ShopActions({ game, onOpen }: { game: Game; onOpen: (panel: Panel) => void }) {
  const { t } = useI18n();
  const { dispatch } = useServices();
  const busy = useStore(game.miniGameUi, (s) => s.run !== null);
  const dragging = useStore(game.placementUi, (s) => s.dragging !== null);
  const room = useSave(currentRoom);
  const shopOpen = useSave((s) => s.shop.isOpen);
  const ordersOn = useSave((s) => s.unlockedIds.includes('unl_npc_orders'));
  // "Đơn hàng •n": n = orders that can be delivered right now (ux-screens §3.4).
  const deliverable = useSave(
    (s) =>
      s.orders.open.filter((o) =>
        o.lines.every(
          (l) =>
            (s.inventory.productStacks[l.itemId] ?? 0) +
              Object.values(s.rooms.minimart.slots).filter(
                (c) => c.kind === 'product' && c.itemId === l.itemId,
              ).length >=
            l.qty,
        ),
      ).length,
  );
  if (busy || dragging || room !== 'minimart') return null;
  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.button}
        data-variant={shopOpen ? 'open' : 'closed'}
        data-testid="shop-toggle"
        aria-pressed={shopOpen}
        onClick={() => {
          dispatch({ type: 'toggleShopOpen', open: !shopOpen });
        }}
      >
        <Icon name={shopOpen ? 'shop_closed' : 'shop_open'} size={26} />
        {shopOpen ? t('ui.shop.close') : t('ui.shop.open')}
      </button>
      {ordersOn && (
        <button
          type="button"
          className={styles.button}
          data-variant="soft"
          data-testid="nav-orders"
          onClick={() => {
            onOpen('orders');
          }}
        >
          <Icon name="orders" size={26} />
          {t('ui.orders.chip')}
          {deliverable > 0 && <span className={styles.badge}>{deliverable}</span>}
        </button>
      )}
    </div>
  );
}

/** In Studio NANO: the Sổ hẹn chip with the number of bookings waiting (studio-nano.md §4). */
export function StudioActions({ game, onOpen }: { game: Game; onOpen: (panel: Panel) => void }) {
  const { t } = useI18n();
  const busy = useStore(game.miniGameUi, (s) => s.run !== null);
  const dragging = useStore(game.placementUi, (s) => s.dragging !== null);
  const room = useSave(currentRoom);
  const waiting = useSave((s) => (s.studio.session ? 0 : s.studio.requests.length));
  if (busy || dragging || room !== 'studio') return null;
  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.button}
        data-variant="soft"
        data-testid="nav-studio-bookings"
        onClick={() => {
          onOpen('studio');
        }}
      >
        <Icon name="orders" size={26} />
        {t('ui.studio.chip')}
        {waiting > 0 && <span className={styles.badge}>{waiting}</span>}
      </button>
    </div>
  );
}
